import { useEffect, useRef, useState } from "react";
import moment from "moment";
import { useMap } from "react-leaflet";
import { gridToAsc, meteomaticsJsonToGrid } from "../../utils/utils";

import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";

import { CACHE_DURATION } from "../../constants/constants";

const MapController = ({ particleLayer, mapRef }) => {
  const map = useMap();
  const [mobileDetect, setMobileDetect] = useState(false);
  const [mapBbox, setMapBbox] = useState("");
  const vectorLayerRef = useRef(null);
  const cachedDataRef = useRef(null);
  const lastFetchTimeRef = useRef(null);
  // Ref to store the timeout ID for delayed particle regeneration
  const zoomTimeoutRef = useRef(null);

  // Cache duration set to 30 minutes - adjust this value to balance freshness of data vs API usage
  // Longer duration means fewer API calls but potentially less up-to-date data

  const username = process.env.REACT_APP_METEOMATICS_USERNAME;
  const password = process.env.REACT_APP_METEOMATICS_PASSWORD;
  const credentials = btoa(`${username}:${password}`);
  const baseUrl = "https://api.meteomatics.com/";
  const defaultBbox = "65,-140_-5,-45:1,1";

  // Function to update bbox when map bounds change
  const updateBbox = () => {
    // Check if map exists and is fully loaded
    if (!map || !map._loaded) return;

    try {
      const bounds = map.getBounds();
      if (!bounds) return;

      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();

      if (!sw || !ne) return;

      // Format coordinates to 2 decimal places
      const swLat = sw.lat.toFixed(2);
      const swLng = sw.lng.toFixed(2);
      const neLat = ne.lat.toFixed(2);
      const neLng = ne.lng.toFixed(2);

      const newBbox = `${neLat},${swLng}_${swLat},${neLng}`;
      setMapBbox(newBbox);
    } catch (error) {
      console.error("Error updating bbox:", error);
    }
  };

  // This function loads or updates the vector layer on the map
  // It uses caching to avoid unnecessary API calls when panning or refreshing the map
  const loadVectorLayer = async () => {
    if (!map || !particleLayer) return;

    if (!map || !map._loaded || typeof map.getZoom !== "function") {
      console.warn("Map or zoom not ready yet");
      return;
    }

    let uAsc, vAsc;
    const now = Date.now();

    const storedU = localStorage.getItem("wind_uAsc");
    const storedV = localStorage.getItem("wind_vAsc");
    const storedTime = localStorage.getItem("wind_lastFetchTime");

    if (storedU && storedV && storedTime) {
      const cacheAge = Date.now() - Number(storedTime);
      if (cacheAge < CACHE_DURATION) {
        cachedDataRef.current = {
          uAsc: JSON.parse(storedU),
          vAsc: JSON.parse(storedV),
        };
        lastFetchTimeRef.current = Number(storedTime);
      }
    }

    // Only fetch new data if we don't have cached data or if the cache has expired
    // This prevents unnecessary API calls when panning or refreshing the map
    const shouldFetch =
      !lastFetchTimeRef.current ||
      now - lastFetchTimeRef.current > CACHE_DURATION;

    if (shouldFetch) {
      // Only generate the datetime and URLs when we need to fetch
      const datetime = moment().utc().format("YYYY-MM-DDTHH:mm:ss[Z]");
      // Use mapBbox if available, otherwise fall back to defaultBbox
      const activeBbox = mapBbox ? `${mapBbox}:1,1` : defaultBbox;
      const uUrl = `${baseUrl}${datetime}/wind_speed_u_10m:ms/${activeBbox}/json`;
      const vUrl = `${baseUrl}${datetime}/wind_speed_v_10m:ms/${activeBbox}/json`;

      try {
        const [uJson, vJson] = await Promise.all([
          fetch("/wp-json/aprilaire/v1/airmap/proxy", {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              meteomaticsUrl: uUrl,
            }),
          }).then((r) => r.json()),
          fetch("/wp-json/aprilaire/v1/airmap/proxy", {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              meteomaticsUrl: vUrl,
            }),
          }).then((r) => r.json()),
        ]);

        uAsc = gridToAsc(meteomaticsJsonToGrid(uJson.data));
        vAsc = gridToAsc(meteomaticsJsonToGrid(vJson.data));

        // Update cache
        cachedDataRef.current = { uAsc, vAsc };
        lastFetchTimeRef.current = now;

        localStorage.setItem("wind_uAsc", JSON.stringify(uAsc));
        localStorage.setItem("wind_vAsc", JSON.stringify(vAsc));
        localStorage.setItem("wind_lastFetchTime", now.toString());
      } catch (error) {
        console.error("Error fetching wind data:", error);
        // If fetch fails but we have cached data, use it
        if (cachedDataRef.current) {
          uAsc = cachedDataRef.current.uAsc;
          vAsc = cachedDataRef.current.vAsc;
        } else {
          return; // No data available
        }
      }
    } else {
      // Use cached data
      uAsc = cachedDataRef.current.uAsc;
      vAsc = cachedDataRef.current.vAsc;
    }

    const vf = window.L.VectorField?.fromASCIIGrids(
      uAsc.toString(),
      vAsc.toString()
    );

    // remove old layer if exist
    if (vectorLayerRef.current) {
      map.removeLayer(vectorLayerRef.current);

      // Stop animation
      if (vectorLayerRef.current._animationFrame) {
        cancelAnimationFrame(vectorLayerRef.current._animationFrame);
      }

      vectorLayerRef.current = null;
    }

    // zoom-dependent config
    const zoom = map.getZoom();

    // Define different parameters for different zoom ranges
    let velocityScale, fade, lineWidth;

    // Range 1: zoom < 7
    if (zoom < 6) {
      velocityScale = 0.0035;
      fade = 0.93;
      lineWidth = 0.8;
    }
    // Range 2: 7 <= zoom <= 10
    else if (zoom >= 6 && zoom <= 10) {
      velocityScale = 0.0021;
      fade = 0.89;
      lineWidth = 0.8;
    }
    // Range 3: zoom > 10
    else {
      velocityScale = 0.006;
      fade = 0.95;
      lineWidth = 0.4;
    }

    // Significantly increase particle count at higher zoom levels
    // At low zoom (4): paths ≈ 2000
    // At high zoom (12): paths ≈ 10000
    const paths = 1000 + Math.pow(zoom * 1.5, 3) * 40;

    // Create a function to generate random particles with different starting positions
    const randomizeParticles = () => {
      // Generate a random offset for each particle
      const randomOffset = Math.random() * 10;
      return {
        paths,
        velocityScale: velocityScale * (0.8 + Math.random() * 0.4), // Randomize velocity scale
        fade, // Use zoom-dependent fade
        lineWidth, // Use zoom-dependent lineWidth
        color: "rgba(255, 255, 255, 0.7)", // More transparent white for a stronger blur-like effect
        maxAge: 100 + Math.floor(Math.random() * 100), // Randomize particle lifetime
        // Add random offset to particle positions
        offsetX: -randomOffset + Math.random() * randomOffset * 100,
        offsetY: -randomOffset + Math.random() * randomOffset * 100,
      };
    };

    // Check if map is still available before creating the layer
    if (!map || !map?._loaded) return;

    try {
      const newLayer = window?.L?.canvasLayer?.vectorFieldAnim(
        vf,
        randomizeParticles()
      );

      // Check again if map is still available before adding the layer
      if (map && map?._loaded) {
        newLayer?.addTo(map);
        vectorLayerRef.current = newLayer;
      }
    } catch (error) {
      console.error("Error creating vector field animation:", error);
    }
  };

  useEffect(() => {
    if (particleLayer) {
      vectorLayerRef.current?.addTo(map);
    } else {
      map.removeLayer(vectorLayerRef.current);
    }
  }, [particleLayer]);

  useEffect(() => {
    mapRef.current = map;

    const script = document.createElement('script');
    script.src = 'https://ihcantabria.github.io/Leaflet.CanvasLayer.Field/dist/leaflet.canvaslayer.field.js';
    script.async = true;
    script.onload = () => {
      // Check if map is available before loading vector layer
      if (map && map._loaded) {
        setTimeout(() => {
          loadVectorLayer();
        }, 100);
      }
    };

    try {
      document.body.appendChild(script);
    } catch (error) {
      console.error("Error appending script to document body:", error);
    }

    return () => {
      try {
        // Check if script is still in the document before removing
        if (script && script.parentNode) {
          document.body.removeChild(script);
        }
      } catch (error) {
        console.error("Error removing script from document body:", error);
      }
    };
  }, [map]); // Add map as a dependency to ensure we have access to it

  useEffect(() => {
    if (!map) return;

    map.setMinZoom(mobileDetect ? 3 : 4);
    map.setMaxZoom(10);
    map.setMaxBounds([
      [-5, -140],
      [65, -45],
    ]);
    map.options.maxBoundsViscosity = 1.0;

    // Initialize bbox based on initial map bounds
    updateBbox();

    const onZoom = () => {
      // Check if map is still available
      if (!map || !map._loaded) return;

      updateBbox(); // Update bbox when zoom changes

      // Remove current particles immediately
      try {
        if (vectorLayerRef.current) {
          map.removeLayer(vectorLayerRef.current);

          // Stop animation
          if (vectorLayerRef.current._animationFrame) {
            cancelAnimationFrame(vectorLayerRef.current._animationFrame);
          }

          vectorLayerRef.current = null;
        }
      } catch (error) {
        // Reset the reference even if removal fails
        vectorLayerRef.current = null;
      }

      // Clear any existing timeout
      if (zoomTimeoutRef.current) {
        clearTimeout(zoomTimeoutRef.current);
      }

      // Delay creating new particles by 1 second
      zoomTimeoutRef.current = setTimeout(() => {
        // Check again if map is still available before reloading
        if (map && map._loaded) {
          loadVectorLayer();
        }
      }, 700);
    };

    const onMove = () => {
      // Check if map is still available
      if (!map || !map._loaded) return;

      updateBbox(); // Update bbox when map is moved
    };

    map.on("zoomend", onZoom);
    map.on("moveend", onMove);

    // Delay first load to avoid mobileDetect change error
    const delay = setTimeout(() => {
      loadVectorLayer();
    }, 700);

    return () => {
      clearTimeout(delay);

      // Safe cleanup of event listeners
      try {
        if (map && map._loaded) {
          map.off("zoomend", onZoom);
          map.off("moveend", onMove);
        }
      } catch (error) {
        console.error("Error removing event listeners:", error);
      }

      // Safe cleanup of vector layer
      try {
        if (map && map._loaded && vectorLayerRef.current) {
          map.removeLayer(vectorLayerRef.current);
        }
      } catch (error) {
        console.error("Error removing vector layer during cleanup:", error);
      }

      // Always reset the reference
      vectorLayerRef.current = null;

      // Clear any pending timeouts
      if (zoomTimeoutRef.current) {
        clearTimeout(zoomTimeoutRef.current);
        zoomTimeoutRef.current = null;
      }
    };
  }, [map, mobileDetect]);

  useEffect(() => {
    const handleResize = () => {
      setMobileDetect(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return null;
};

export default MapController;
